import {
  Button,
  List,
  Title,
  Separator,
  Caption,
} from '@yaradigitallabs/ahua-react';
import { useMemo, useState } from 'react';
import { SelectedSoftTagListItem } from './SelectedSoftTagListItem';
import { Colors } from '../../../../public/GlobalTokens';
import { SelectedSoftTagEmptyState } from './SelectedSoftTagEmptyState';
import { useNotification } from '@/components/Notification/useNotification';
import type { SoftTag } from '../utils/match-maker.types';
import { MAX_SELECTED_SOFT_TAGS } from '../utils/match-maker.constants';

interface SelectedSoftTagListProps {
  items: SoftTag[];
  onChange: (items: SoftTag[]) => void;
  onRemoveItem?: (item: SoftTag) => void;
  onClearAll?: () => void;
  onConfirm?: (items: SoftTag[]) => void;
  tagMappingLoading?: boolean;
  inUpdateMode?: boolean;
}

const normalizeSelectedSoftTags = (items: SoftTag[]): SoftTag[] => {
  return items.map((item, index) => ({
    ...item,
    priority: index + 1,
  }));
};

export const SelectedSoftTagList = ({
  items,
  onChange,
  onRemoveItem,
  onClearAll,
  onConfirm,
  tagMappingLoading,
  inUpdateMode = false,
}: SelectedSoftTagListProps) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const showNotification = useNotification();

  const selectedSoftTags = useMemo(
    () => normalizeSelectedSoftTags(items),
    [items]
  );

  const handleDropOnItem = (targetId: string) => {
    if (!draggingId || draggingId === targetId) {
      return;
    }

    const fromIndex = selectedSoftTags.findIndex(
      item => item.timeseries_id === draggingId
    );
    const toIndex = selectedSoftTags.findIndex(
      item => item.timeseries_id === targetId
    );

    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    const next = [...selectedSoftTags];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    onChange(normalizeSelectedSoftTags(next));

    if (fromIndex !== 0 && toIndex === 0) {
      showNotification('Soft tag set to primary', 'success');
    }
  };

  const handleRemove = (removedItem: SoftTag) => {
    if (onRemoveItem) {
      onRemoveItem(removedItem);
      return;
    }

    onChange(
      selectedSoftTags.filter(
        item => item.timeseries_id !== removedItem.timeseries_id
      )
    );
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const handleConfirm = () => {
    onConfirm?.(selectedSoftTags);
  };

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
      return;
    }

    onChange([]);
  };

  return (
    <div className="flex h-full w-full min-h-0 flex-col">
      <div className="flex flex-col lg:!flex-row justify-between items-end lg:!items-center mb-2">
        <div className="flex flex-row gap-3 items-center">
          <Title size="l">Selected soft tags</Title>
          {selectedSoftTags.length > 0 && (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white">
              {selectedSoftTags.length}
            </span>
          )}
        </div>
        {selectedSoftTags.length > 0 && (
          <Button
            colorConcept="brand"
            size="s"
            variant="ghost"
            onClick={handleClearAll}
          >
            Clear all
          </Button>
        )}
      </div>
      {selectedSoftTags.length === 0 ? (
        <>
          <SelectedSoftTagEmptyState />
          {inUpdateMode && (
            <Button
              colorConcept="brand"
              size="n"
              variant="primary"
              onClick={handleConfirm}
              className="mt-4!"
            >
              Confirm
            </Button>
          )}
        </>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-auto mb-4 bg-white rounded-md items-center border border-gray-200">
            <div className="p-4">
              <Caption css={{ color: Colors.colorType.grayscale.contrast }}>
                {`Drag and drop soft tags to reorder them. You can select up to ${MAX_SELECTED_SOFT_TAGS}.`}
              </Caption>
            </div>
            <Separator />
            <List.Middle>
              {selectedSoftTags.map(item => (
                <div
                  key={item.timeseries_id}
                  onDragOver={event => {
                    event.preventDefault();
                  }}
                  onDrop={() => {
                    handleDropOnItem(item.timeseries_id);
                    handleDragEnd();
                  }}
                >
                  <SelectedSoftTagListItem
                    item={item}
                    isDragging={draggingId === item.timeseries_id}
                    onRemove={handleRemove}
                    onDragStart={setDraggingId}
                    onDragEnd={handleDragEnd}
                  />
                </div>
              ))}
            </List.Middle>
          </div>
          <Button
            colorConcept="brand"
            size="n"
            variant="primary"
            onClick={handleConfirm}
            loading={tagMappingLoading}
            loadingPosition="leading"
          >
            Confirm
          </Button>
        </>
      )}
    </div>
  );
};
